import { useState } from "react";

import Body from "client/common/components/layouts/Body";
import Container from "client/common/components/layouts/Container";
import PillButton from "client/common/components/buttons/PillButton";
import HorizontalOverflow from "client/common/components/display/HorizontalOverflow";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorContainer from "client/common/components/errors/FetchErrorContainer";
import { useClinic } from "client/clinics/hooks";
import { urls } from "client/routes";
import { useProductLinesViewOnVisible, useViewItemListOnVisible } from "client/analytics/utils";
import { ProductLineListSource } from "client/analytics/types";
import CatalogFilters from "client/home/components/CatalogFilters";
import DealsLink from "client/home/components/DealsLink";

import ProductLineCard from "./ProductLineCard";
import BrowseSupport from "./BrowseSupport";
import { useBrowseQuery } from "../hooks";
import PreFooter from "./PreFooter";
import CategoryCard from "./CategoryCard";
import {
  BrowseAllPatientKind,
  BrowseProductLine,
  IBrowseFilterSpeciesKind,
  IClientBrowseCategory,
} from "../types";

import styles from "../styles/BrowseCatalog.module.less";

type FeaturedSectionProps = {
  productLines: BrowseProductLine[];
};

const FeaturedSection = ({ productLines }: FeaturedSectionProps) => {
  const itemListRef = useViewItemListOnVisible(
    productLines,
    ProductLineListSource.BrowseCatalogFeatured,
  );
  const productLinesViewRef = useProductLinesViewOnVisible(
    productLines,
    ProductLineListSource.BrowseCatalogFeatured,
  );

  return (
    <div className={styles.category}>
      <div className={styles.info}>
        <div className={styles.titles}>
          <h3 className={styles.title}>
            Browse <span className={styles.accent}>popular</span> medications
          </h3>
        </div>
      </div>
      <HorizontalOverflow
        ref={itemListRef}
        direction="right"
        classNames={{ scroller: styles.products, button: styles.button }}
      >
        <CategoryCard.TopSellers className={styles.card} />
        {productLines.map((productLine, index) => (
          <div
            key={productLine.id}
            ref={index === 0 ? productLinesViewRef : undefined}
            className={styles.card}
          >
            <ProductLineCard
              productLine={productLine}
              eventViewName={ProductLineListSource.BrowseCatalogFeatured}
              eventCategoryId={null}
            />
          </div>
        ))}
      </HorizontalOverflow>
    </div>
  );
};

type CategorySectionProps = {
  category: IClientBrowseCategory;
};

const CategorySection = ({ category }: CategorySectionProps) => {
  const itemListRef = useViewItemListOnVisible(
    category.product_lines,
    `${ProductLineListSource.BrowseCatalogCategory} - ${category.name}`,
  );
  const productLinesViewRef = useProductLinesViewOnVisible(
    category.product_lines,
    ProductLineListSource.BrowseCatalogCategory,
    category.id,
  );

  return (
    <div className={styles.category}>
      <div className={styles.info}>
        <div className={styles.titles}>
          <h3 className={styles.title}>{category.extended_name}</h3>
        </div>
        <PillButton
          size="s"
          variant="secondary"
          dest={urls.products.category({ params: { id: category.id } })}
        >
          View All
        </PillButton>
      </div>
      <HorizontalOverflow
        ref={itemListRef}
        direction="right"
        classNames={{ scroller: styles.products, button: styles.button }}
      >
        <CategoryCard category={category} className={styles.card} />
        {category.product_lines.map((productLine, index) => (
          <div
            key={productLine.id}
            ref={index === 0 ? productLinesViewRef : undefined}
            className={styles.card}
          >
            <ProductLineCard
              productLine={productLine}
              eventViewName={ProductLineListSource.BrowseCatalogCategory}
              eventCategoryId={category.id}
            />
          </div>
        ))}
      </HorizontalOverflow>
    </div>
  );
};

const BrowseCatalogV1 = () => {
  const [species, setSpecies] = useState<IBrowseFilterSpeciesKind>(BrowseAllPatientKind.ALL);
  const clinic = useClinic();
  const { data: browse, isLoading, error } = useBrowseQuery({ species }, { enabled: true });

  if (isLoading) {
    return <LoadingContainer />;
  }

  if (error || !browse) {
    return <FetchErrorContainer error={error} entityName="categories" isPlural={true} />;
  }

  return (
    <div>
      <Body>
        <div className={styles.top}>
          <div className={styles.heading}>
            <div className={styles.titles}>
              <div className={styles.label}>Welcome to our new home delivery portal</div>
              <h1 className={styles.title}>Browse online store</h1>
            </div>
            <div className={styles.actions}>
              <DealsLink />
              <CatalogFilters species={species} setSpecies={setSpecies} clinic={clinic} />
            </div>
          </div>
          <div className={styles.subheading}>
            <HorizontalOverflow
              direction="right"
              classNames={{ scroller: styles.categories, next: styles.next, back: styles.back }}
            >
              {browse.categories.map((category) => (
                <CategoryCard.Small
                  key={category.id}
                  category={category}
                  className={styles.smallCard}
                />
              ))}
            </HorizontalOverflow>
          </div>
        </div>
      </Body>
      <div className={styles.main}>
        <Container>
          <div className={styles.categories}>
            <FeaturedSection productLines={browse.featured_product_lines} />
            {browse.categories.map((category) => (
              <CategorySection key={category.id} category={category} />
            ))}
          </div>
        </Container>
      </div>
      <div className={styles.support}>
        <Container>
          <BrowseSupport />
        </Container>
      </div>
      <PreFooter image={clinic.storefront_prefooter_image} />
    </div>
  );
};

export default BrowseCatalogV1;
